'use strict';

const OVERRIDES_STORAGE_KEY = 'rp:cache-ttl-overrides';

/**
 * LocalStorage key for the shared buyer-location cache (header, PDP, find-dealer).
 *
 * Stored value shape:
 * @typedef {object} CachedLocation
 * @property {string} postalCode               Trimmed postal/ZIP shown in the UI.
 * @property {object} location                 Raw IPLocation values from the server (postal_code, city, region_code, etc.).
 * @property {'browser'|'ip'|'manual'} source  Which tier produced this entry. See `location-source.js`.
 *
 * Writers must call `localStorageSlim.set(LOCATION_CACHE_KEY, payload, { ttl: cacheConfig.getTtlFor('location') })`.
 */
const LOCATION_CACHE_KEY = 'rp:location:v1';

/**
 * Suppresses `/ajax/ip-location/update` retries after a server-side failure, keyed by
 * the lat/long pair (rounded to 5 decimals). See `geolocation.js`.
 */
const LOCATION_FAILED_COORDS_CACHE_KEY = 'rp:location:failed-coords:v1';

/** @type {Readonly<Record<string, number>>} */
const DEFAULT_TTL_SECONDS = Object.freeze({
	cart: 600, // 10 minutes
	csrf: 300, // 5 minutes
	garage: 1800, // 30 minutes
	location: 600, // 10 minutes (manual ZIP cache, see location-modal-update)
	locationFail: 3600, // 1 hour (failed browser-coord retry suppression, see geolocation.js)
	profile: 600, // 10 minutes
	recentlyViewed: 1800, // 30 minutes
	supplier: 600, // 10 minutes
});

/**
 * @returns {Record<string, number>}
 */
function getOverrides() {
	try {
		if (globalThis.localStorage === undefined || !globalThis.localStorage) {
			return {};
		}
		const raw = globalThis.localStorage.getItem(OVERRIDES_STORAGE_KEY);
		if (!raw) {
			return {};
		}
		const parsed = JSON.parse(raw);
		return typeof parsed === 'object' && parsed !== null ? parsed : {};
	} catch {
		return {};
	}
}

/**
 * @param {Record<string, number>} overrides
 */
function setOverrides(overrides) {
	try {
		if (globalThis.localStorage === undefined || !globalThis.localStorage) {
			return;
		}
		globalThis.localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(overrides));
	} catch {
		// no-op
	}
}

/**
 * @param {string} name 'cart' | 'csrf' | 'garage' | 'location' | 'profile' | 'recentlyViewed' | 'supplier'
 * @returns {number} TTL in seconds
 */
function getTtlFor(name) {
	const overrides = getOverrides();
	if (typeof overrides[name] === 'number' && overrides[name] >= 0) {
		return overrides[name];
	}
	const defaultTtl = DEFAULT_TTL_SECONDS[name];
	return typeof defaultTtl === 'number' ? defaultTtl : 600;
}

/**
 * @param {string} name 'cart' | 'csrf' | 'garage' | 'location' | 'profile' | 'recentlyViewed' | 'supplier'
 * @param {number} ttlSeconds TTL in seconds
 */
function setTtlFor(name, ttlSeconds) {
	if (!Number.isInteger(ttlSeconds) || ttlSeconds < 0) {
		throw new Error('TTL must be a positive integer or 0');
	}

	const overrides = getOverrides();
	const next = { ...overrides };
	next[name] = ttlSeconds;

	try {
		if (globalThis.localStorage === undefined || !globalThis.localStorage) {
			return;
		}
		globalThis.localStorage.clear();
		if (Object.keys(next).length > 0) {
			setOverrides(next);
		}
		globalThis.location.reload();
	} catch {
		// no-op
	}
}

module.exports = {
	DEFAULT_TTL_SECONDS,
	getOverrides,
	getTtlFor,
	LOCATION_CACHE_KEY,
	LOCATION_FAILED_COORDS_CACHE_KEY,
	setTtlFor,
};
